import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_MOTIVOS_NO_AVANCE } from './mockDataEjemplos';

// Catálogo editable (Administración > Datos Maestros > Motivos de Rechazo, ver
// GestionMotivosRechazo) — sin acción sugerida hardcodeada por motivo, salvo estos 3
// conocidos desde el inicio. Un motivo nuevo que el admin agregue simplemente no
// muestra sugerencia (ver getOrDefault más abajo).
const ACCION_POR_MOTIVO = {
    'Falta documentación': 'Reforzar el checklist de documentación al confirmar el turno.',
    'No puede pagar moratoria': 'Evaluar planes de pago más accesibles o explicarlos antes del turno.',
    'Solo consulta': 'Considerar un canal de consulta rápida que no ocupe un turno completo.',
    'Otro': 'Motivo sin clasificar — revisar si conviene sumarlo como categoría propia del catálogo.',
};

/**
 * Motivos de no avance — ranking (sin torta, son varias categorías donde importa el orden).
 * @param {{data: Array<{motivo: string, motivoLabel: string, cantidad: number, porcentaje: number}>}} props
 */
const MotivosNoAvance = ({ data, compacto = false }) => {
    const filas = useMemo(() => [...(data || [])].sort((a, b) => b.cantidad - a.cantidad), [data]);
    const total = useMemo(() => filas.reduce((acc, f) => acc + f.cantidad, 0), [filas]);
    const maxCantidad = useMemo(() => Math.max(1, ...filas.map((f) => f.cantidad)), [filas]);
    const otro = filas.find((f) => f.motivo === 'Otro');
    const pctOtro = otro && total ? (otro.cantidad / total) * 100 : 0;

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Motivos de no avance"
                        queMuestra="Te dice, cuando una atención termina SIN abrir un trámite, cuál fue la razón más frecuente — para saber si el problema es de un tipo que se puede resolver (documentación, plata) o si en realidad ni buscaban un trámite."
                        ejemploVisual={<MotivosNoAvance compacto data={MOCK_MOTIVOS_NO_AVANCE} />}
                        comoLeerlo={[
                            'Cada fila es un motivo del catálogo (editable desde Administración), ordenado de mayor a menor cantidad.',
                            'El largo de la barra es relativo al motivo más frecuente del período, no a un total fijo.',
                            'Todas las barras van en azul salvo "Otro", que va en gris — no hay jerarquía de bueno/malo entre motivos, el gris solo distingue el motivo "sin clasificar" del resto.',
                            'Cuando el motivo tiene una acción sugerida cargada (no todos la tienen), aparece como texto chico debajo de su barra.',
                        ]}
                        analisis="'Solo consulta' es, por lejos, el motivo más común (44.4%) — esas personas nunca buscaron abrir un trámite ese día, así que no es una atención 'perdida' en el sentido de una oportunidad desaprovechada, sino demanda de un tipo distinto. 'Falta documentación' (26.7%) sí es accionable: si se refuerza el checklist antes del turno, una parte de ese grupo podría convertir en la próxima visita en lugar de volver a quedar en este mismo motivo."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Motivos de no avance</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                        Por qué una atención terminó sin abrir un trámite.
                    </Typography>
                </>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: compacto ? '8px' : '14px' }}>
                {filas.map((f) => {
                    const esOtro = f.motivo === 'Otro';
                    const color = esOtro ? COLOR.grisFrio : COLOR.azul;
                    return (
                        <Box key={f.motivo}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.negro }}>{f.motivoLabel}</Typography>
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 13 }}>
                                    <b style={{ color: COLOR.negro }}>{Number(f.porcentaje).toFixed(1)}%</b>{' '}
                                    <span style={{ color: COLOR.grisTexto }}>({f.cantidad})</span>
                                </Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 4, background: COLOR.gris, marginTop: '4px', overflow: 'hidden' }}>
                                <Box sx={{ width: `${(f.cantidad / maxCantidad) * 100}%`, height: '100%', background: color }} />
                            </Box>
                            {!compacto && ACCION_POR_MOTIVO[f.motivo] && (
                                <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, marginTop: '4px' }}>
                                    {ACCION_POR_MOTIVO[f.motivo]}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
            {!compacto && otro && total >= 20 && (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto, marginTop: '16px' }}>
                    "{otro.motivoLabel}" representa el {pctOtro.toFixed(0)}% del total — conviene cerrar el campo a una lista de
                    opciones para que ese porcentaje se pueda accionar.
                </Typography>
            )}
        </Card>
    );
};

export default MotivosNoAvance;
